import { useEffect, useState, type ChangeEvent } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';

import { Dialog, LoadingButton, Textarea } from '@myn/mui';

import { useUpdateVersionMutation } from '../../../hooks/useBuyingPlanVersionMutation.js';
import useSimpleNotification from '../../../hooks/useSImpleNotification.js';

type ICommentModalProps = {
  id?: number;
  description?: string;
  isOpened: boolean;
  onClose: () => void;
};

const CommentModal: React.FC<ICommentModalProps> = ({
  id,
  description,
  isOpened,
  onClose,
}) => {
  const { t } = useTranslation('@myn/planning');

  const { mutateAsync: updateVersion, isPending } = useUpdateVersionMutation();
  const isReadOnlyUser = !useIsAllowedPermission(
    'campaignPlanningFeature:modify',
  );
  const { showErrorNotification, showSuccessNotification } =
    useSimpleNotification();

  const [comment, setComment] = useState<string | null>('');

  const isSubmitButtonDisabled = isReadOnlyUser || comment === description;

  const handleCloseModal = () => {
    setComment(description ?? '');
    onClose();
  };

  const handleCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) =>
    setComment(e.target.value);

  const handleSaveComment = async () => {
    if (!id) {
      return;
    }

    try {
      await updateVersion({ versionId: id, description: comment ?? '' });
      showSuccessNotification({
        title: t('Success'),
        subTitle: t('Description was changed'),
      });
      onClose();
    } catch {
      showErrorNotification({
        title: t('Error'),
        subTitle: t('Description was not changed'),
      });
    }
  };

  useEffect(() => {
    setComment(description ?? '');
  }, [id, description]);

  return (
    <Dialog
      open={isOpened}
      onClose={handleCloseModal}
      title={t('Description')}
      cancelButtonProps={{
        onClick: handleCloseModal,
      }}
      submitButton={
        <LoadingButton
          loading={isPending}
          size="large"
          variant="contained"
          color="primary"
          onClick={handleSaveComment}
          disabled={isSubmitButtonDisabled}
        >
          {t('Save')}
        </LoadingButton>
      }
    >
      <Textarea fullWidth value={comment} onChange={handleCommentChange} />
    </Dialog>
  );
};

export default CommentModal;
